import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Chip } from '@mui/material'

import languageService from '../../../../../services/LanguageService'
import { TagList } from '../../../../feature/components/TagList/TagList'
import { useLiveEventTagGroupsWithTags } from '../../../hooks/useLiveEventTagGroups'
import { LiveEventCalendarAdditionalDataId } from '../../../types/LiveEventAdditionalCalendarData'
import { LiveEventAppearance } from '../../../types/LiveEventAppearance'
import { LiveEventDuration } from '../../../types/LiveEventDuration'
import { LiveEventPricePoint } from '../../../types/LiveEventPricePoint'
import { LiveEventTrackerTab } from '../../../types/LiveEventTrackerTab'
import { TLiveEventsCommonFilters, LiveEventsCommonFiltersData } from '../../../types/LiveEventsCommonFilters'
import { resolveAdditionalDataDisabledState } from '../../../utils/utils'
import LiveEventsSecondaryTaxonomyTagList from '../../LiveEventsSecondaryTaxonomyTagList/LiveEventsSecondaryTaxonomyTagList'

/**
 * Component for displaying the common LET filters that are currently active
 */
type ActiveCommonFiltersProps = {
  filters: TLiveEventsCommonFilters
  filtersData: LiveEventsCommonFiltersData
}

export const ActiveCommonFilters: FC<ActiveCommonFiltersProps> = ({ filters, filtersData }) => {
  const { t } = useTranslation()
  const { subpage = LiveEventTrackerTab.Feed } = useParams<{ subpage: LiveEventTrackerTab }>()
  const { data: liveEventTagGroupsWithTags } = useLiveEventTagGroupsWithTags()

  // Data-based visibility - only show filter chips if the corresponding data exists
  const showGameData = filtersData.additionalDatas && filtersData.additionalDatas.length > 0
  const showDuration = filtersData.liveEventDurations && filtersData.liveEventDurations.length > 0
  const showAppearance = filtersData.liveEventAppearances && filtersData.liveEventAppearances.length > 0
  const showPricePoints = filtersData.liveEventPricePoints && filtersData.liveEventPricePoints.length > 0
  const showMotivations = filtersData.motivations && filtersData.motivations.length > 0

  return (
    <>
      {showGameData &&
        (Object.keys(filters.additionalDatas) as LiveEventCalendarAdditionalDataId[]).map((id) => {
          const disabled = resolveAdditionalDataDisabledState(subpage, id)
          return (
            <Chip
              key={id}
              label={t(filtersData.additionalDatas.find((additionalData) => additionalData.id === id)?.name || '')}
              disabled={disabled}
              color="secondary"
            />
          )
        })}
      {filters.liveEventTags.length > 0 && (
        <TagList selectedTags={filters.liveEventTags} tagGroups={liveEventTagGroupsWithTags} variant="small" selectedOnly hideGroupTitles />
      )}
      {filters.liveEventSecondaryTags.length > 0 && (
        <div className="secondaryTagList">
          <LiveEventsSecondaryTaxonomyTagList tags={filters.liveEventSecondaryTags.map((tag) => tag.id)} justifyContent="flex-start" />
        </div>
      )}
      {showMotivations &&
        Object.keys(filters.motivations).map((motivationType) => {
          return <Chip key={motivationType} label={languageService.getTranslation('motivations', motivationType)} />
        })}
      {showMotivations &&
        Object.keys(filters.archetypes).map((motivationKey) => {
          return <Chip key={motivationKey} label={languageService.getTranslation('motivations', motivationKey)} />
        })}
      {showDuration &&
        Object.keys(filters.liveEventDurations).map((id) => {
          const duration = filtersData.liveEventDurations.find((duration) => duration.id === id) as LiveEventDuration
          const label = duration.start > 1 ? `${duration?.name} ${t('common:days')}` : `${duration?.name} ${t('common:day')}`
          return <Chip key={id} label={label} color="secondary" />
        })}
      {showAppearance &&
        Object.keys(filters.liveEventAppearances).map((id) => {
          const appearance = filtersData.liveEventAppearances.find((appearance) => appearance === id) as LiveEventAppearance
          return <Chip key={id} label={t('live-events:appearance_event_type_' + appearance)} color="secondary" />
        })}
      {showPricePoints &&
        Object.keys(filters.liveEventPricePoints).map((id) => {
          const pricePoint = filtersData.liveEventPricePoints.find((pricePoint) => pricePoint.id === id) as LiveEventPricePoint
          const label = pricePoint.name
          return <Chip key={id} label={label} color="secondary" />
        })}
    </>
  )
}
